export type FilterValue = string | number | Date | string[] | number[]

export type DateRangeType =
  | 'all'
  | '7d'
  | '30d'
  | '60d'
  | '90d'
  | '6m'
  | '1y'
  | { from?: string | Date; to?: string | Date }

export type FilterOperator =
  | 'equals'
  | 'not_equals'
  | 'greater_than'
  | 'greater_than_or_equal'
  | 'less_than'
  | 'less_than_or_equal'
  | 'between'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'in'
  | 'not_in'
  | 'before'
  | 'after'

export interface Filter {
  field: string
  operator: FilterOperator
  value: FilterValue
  value2?: FilterValue
}

export interface SchemaField {
  field: string
  label: string
}

interface LegendItem {
  color: string
  label: string
}

interface ExportOptions {
  projectName?: string
  title?: string
  filters?: Filter[]
  schema?: SchemaField[]
  dateRange?: DateRangeType
  dataDateRange?: { from: Date | string; to: Date | string }
}

function formatFilterValue(value: FilterValue): string {
  if (Array.isArray(value)) {
    return value.length > 3
      ? `${value.slice(0, 3).join(', ')}... (+${value.length - 3} more)`
      : value.join(', ')
  }
  if (value instanceof Date) {
    return value.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }
  return String(value)
}

function formatOperator(operator: FilterOperator): string {
  const operatorMap: Record<FilterOperator, string> = {
    equals: '=',
    not_equals: '≠',
    greater_than: '>',
    greater_than_or_equal: '≥',
    less_than: '<',
    less_than_or_equal: '≤',
    between: 'between',
    contains: 'contains',
    not_contains: 'does not contain',
    starts_with: 'starts with',
    ends_with: 'ends with',
    in: 'in',
    not_in: 'not in',
    before: 'before',
    after: 'after',
  }
  return operatorMap[operator] || operator
}

function formatDateRange(dateRange: DateRangeType): string {
  if (!dateRange || dateRange === 'all') return 'All time'

  const rangeMap: Record<string, string> = {
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
    '60d': 'Last 60 days',
    '90d': 'Last 90 days',
    '6m': 'Last 6 months',
    '1y': 'Last year',
  }

  if (typeof dateRange === 'string') {
    return rangeMap[dateRange] || dateRange
  }

  if (typeof dateRange === 'object' && (dateRange.from || dateRange.to)) {
    const from = dateRange.from
      ? new Date(dateRange.from).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : '...'
    const to = dateRange.to
      ? new Date(dateRange.to).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : '...'
    return `${from} - ${to}`
  }

  return 'All time'
}

function sanitizeFilename(str: string): string {
  return str
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export function generateChartFilename(
  projectName?: string,
  title?: string,
  dateRange?: DateRangeType,
): string {
  const parts: string[] = []

  if (projectName) {
    parts.push(sanitizeFilename(projectName))
  }

  if (title) {
    parts.push(sanitizeFilename(title))
  }

  if (dateRange && dateRange !== 'all') {
    const dateStr = formatDateRange(dateRange)
    if (dateStr !== 'All time') {
      parts.push(sanitizeFilename(dateStr))
    }
  }

  const filename = parts.join(' - ')
  return filename ? `${filename}.png` : 'chart-export.png'
}

function formatFiltersText(
  filters: Filter[],
  schema?: SchemaField[],
  dateRange?: DateRangeType,
  dataDateRange?: { from: Date | string; to: Date | string },
): string[] {
  const lines: string[] = []

  if (dataDateRange) {
    const from = new Date(dataDateRange.from).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
    const to = new Date(dataDateRange.to).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
    lines.push(`Date: ${from} - ${to}`)
  } else if (dateRange && dateRange !== 'all') {
    lines.push(`Date: ${formatDateRange(dateRange)}`)
  }

  if (filters && filters.length > 0) {
    filters.forEach((filter: Filter) => {
      const fieldSchema = schema?.find(
        (s: SchemaField) => s.field === filter.field,
      )
      const fieldLabel = fieldSchema?.label || filter.field
      const operator = formatOperator(filter.operator)
      const value = formatFilterValue(filter.value)

      if (filter.operator === 'between' && filter.value2) {
        const value2 = formatFilterValue(filter.value2)
        lines.push(`${fieldLabel}: ${value} - ${value2}`)
      } else {
        lines.push(`${fieldLabel} ${operator} ${value}`)
      }
    })
  }

  return lines
}

function extractLegendData(
  containerRef: React.RefObject<HTMLElement | null>,
): LegendItem[] | null {
  const legendElement = containerRef.current?.querySelector('.absolute.right-2')

  if (!legendElement) {
    console.log('No legend element found')
    return null
  }

  const items: LegendItem[] = []
  const buttons = legendElement.querySelectorAll('button')
  console.log('Found buttons:', buttons.length)

  buttons.forEach((button: Element) => {
    const divs = button.querySelectorAll('div')
    const spans = button.querySelectorAll('span')

    console.log('Button has divs:', divs.length, 'spans:', spans.length)

    let colorDot: HTMLElement | null = null
    for (const div of divs) {
      const htmlDiv = div as HTMLElement
      const bgColor = window.getComputedStyle(htmlDiv).backgroundColor
      if (
        bgColor &&
        bgColor !== 'rgba(0, 0, 0, 0)' &&
        bgColor !== 'transparent'
      ) {
        colorDot = htmlDiv
        console.log('Found color dot with color:', bgColor)
        break
      }
    }

    let labelSpan: HTMLElement | null = null
    for (const span of spans) {
      const htmlSpan = span as HTMLElement
      if (htmlSpan.textContent?.trim()) {
        labelSpan = htmlSpan
        console.log('Found label span:', htmlSpan.textContent.trim())
        break
      }
    }

    if (colorDot && labelSpan) {
      const color = window.getComputedStyle(colorDot).backgroundColor
      const label = labelSpan.textContent?.trim() || ''
      console.log('Adding legend item:', { color, label })
      items.push({ color, label })
    }
  })

  console.log('Total legend items:', items.length)
  return items.length > 0 ? items : null
}

function drawLegend(
  ctx: CanvasRenderingContext2D,
  legendItems: LegendItem[],
  x: number,
  y: number,
  scale: number,
): void {
  const fontSize = 12 * scale
  const lineHeight = 24 * scale
  const dotSize = 12 * scale
  const padding = 16 * scale
  const itemSpacing = 8 * scale

  ctx.fillStyle = '#f9fafb'
  ctx.strokeStyle = '#e5e7eb'
  ctx.lineWidth = 1 * scale

  const maxLabelWidth = Math.max(
    ...legendItems.map((item: LegendItem) => {
      ctx.font = `400 ${fontSize}px "Geist Sans", sans-serif`
      return ctx.measureText(item.label).width
    }),
  )

  const boxWidth = padding * 2 + dotSize + itemSpacing + maxLabelWidth
  const boxHeight = padding * 2 + legendItems.length * lineHeight

  ctx.fillRect(x, y, boxWidth, boxHeight)
  ctx.strokeRect(x, y, boxWidth, boxHeight)

  legendItems.forEach((item: LegendItem, i: number) => {
    const itemY = y + padding + i * lineHeight

    ctx.fillStyle = item.color
    ctx.beginPath()
    ctx.arc(
      x + padding + dotSize / 2,
      itemY + dotSize / 2,
      dotSize / 2,
      0,
      Math.PI * 2,
    )
    ctx.fill()

    ctx.fillStyle = '#6b7280'
    ctx.font = `400 ${fontSize}px "Geist Sans", sans-serif`
    ctx.textBaseline = 'middle'
    ctx.fillText(
      item.label,
      x + padding + dotSize + itemSpacing,
      itemY + dotSize / 2,
    )
  })
}

// Clone an on-page chart SVG with the document's stylesheets inlined, so the
// serialized markup keeps its CSS-driven colors when drawn to a canvas.
function cloneSvgWithInlineStyles(svgElement: SVGSVGElement): SVGElement {
  const clone = svgElement.cloneNode(true) as SVGElement

  clone.style.color = '#000000'
  clone.style.background = 'transparent'
  clone.style.fontFamily = '"Geist Sans", sans-serif'

  const styleElement = document.createElement('style')
  const styles = Array.from(document.styleSheets)
    .map((sheet) => {
      try {
        return Array.from(sheet.cssRules)
          .map((rule) => rule.cssText)
          .join('')
      } catch (e) {
        return ''
      }
    })
    .join('')

  styleElement.textContent = styles
  clone.prepend(styleElement)
  return clone
}

function svgSize(svgElement: SVGSVGElement): { width: number; height: number } {
  return {
    width: svgElement.viewBox.baseVal.width || svgElement.width.baseVal.value,
    height:
      svgElement.viewBox.baseVal.height || svgElement.height.baseVal.value,
  }
}

export interface ChartImageSnapshot {
  dataUrl: string
  width: number
  height: number
}

// Rasterize a live chart SVG to a JPEG data URL on a white background. Used
// by the chat PDF exporter, which lays out its own titles and captions.
// JPEG, not PNG: jsPDF embeds JPEG bytes verbatim, while a canvas RGBA PNG
// gets decoded to near-raw pixels plus an alpha mask — measured at ~100x the
// size for a typical chart. The white fill below makes alpha irrelevant.
export function svgToJpegDataUrl(
  svgElement: SVGSVGElement,
  scale = 2,
  quality = 0.92,
): Promise<ChartImageSnapshot> {
  const clone = cloneSvgWithInlineStyles(svgElement)
  const { width, height } = svgSize(svgElement)

  const canvas = document.createElement('canvas')
  canvas.width = width * scale
  canvas.height = height * scale
  const ctx = canvas.getContext('2d')
  if (!ctx) return Promise.reject(new Error('Unable to get canvas context'))

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const svgData = new XMLSerializer().serializeToString(clone)
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(svgBlob)
  const img = new Image()

  return new Promise((resolve, reject) => {
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(url)
      resolve({
        dataUrl: canvas.toDataURL('image/jpeg', quality),
        width,
        height,
      })
    }
    img.onerror = (error) => {
      URL.revokeObjectURL(url)
      reject(error)
    }
    img.src = url
  })
}

export function exportChartAsPNG(
  containerRef: React.RefObject<HTMLElement | null>,
  filename = 'chart-export.png',
  options: ExportOptions = {},
): void {
  const {
    projectName,
    title,
    filters = [],
    schema,
    dateRange,
    dataDateRange,
  } = options

  if (!containerRef?.current) {
    console.error('Container reference is not available')
    return
  }

  const svgElement = containerRef.current.querySelector('svg')
  if (!svgElement) {
    console.error('No SVG element found to export')
    return
  }

  const legendData = extractLegendData(containerRef)
  console.log('Legend data extracted:', legendData)

  const {
    clone,
    width: svgWidth,
    height: svgHeight,
  } = cloneSvgWithStyles(svgElement)

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    console.error('Unable to get canvas context')
    return
  }

  const scale = 2
  const width = svgWidth
  const height = svgHeight
  const padding = 40 * scale
  const lineHeight = 20 * scale
  const projectNameFontSize = 28 * scale
  const titleFontSize = 20 * scale
  const filterFontSize = 14 * scale

  const filterLines = formatFiltersText(
    filters,
    schema,
    dateRange,
    dataDateRange,
  )
  const hasMetadata = projectName || title || filterLines.length > 0

  const metadataHeight = hasMetadata
    ? padding +
      (projectName ? projectNameFontSize + 15 * scale : 0) +
      (title ? titleFontSize + 10 * scale : 0) +
      filterLines.length * lineHeight +
      padding
    : 0

  canvas.width = width * scale
  canvas.height = height * scale + metadataHeight

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  let currentY = padding

  if (projectName) {
    ctx.fillStyle = '#000000'
    ctx.font = `700 ${projectNameFontSize}px "Geist Sans", sans-serif`
    ctx.textAlign = 'left'
    ctx.fillText(projectName, padding, currentY)
    currentY += projectNameFontSize + 15 * scale
  }

  if (title) {
    ctx.fillStyle = '#000000'
    ctx.font = `600 ${titleFontSize}px "Geist Sans", sans-serif`
    ctx.textAlign = 'left'
    ctx.fillText(title, padding, currentY)
    currentY += titleFontSize + 10 * scale
  }

  if (filterLines.length > 0) {
    ctx.fillStyle = '#666666'
    ctx.font = `400 ${filterFontSize}px "Geist Sans", sans-serif`
    filterLines.forEach((line) => {
      ctx.fillText(line, padding, currentY)
      currentY += lineHeight
    })
  }

  svgElementToImage(clone)
    .then(({ image, revoke }) => {
      ctx.drawImage(
        image,
        0,
        metadataHeight,
        svgWidth * scale,
        svgHeight * scale,
      )
      if (legendData) {
        drawLegend(
          ctx,
          legendData,
          (svgWidth - 210) * scale,
          metadataHeight + 30 * scale,
          scale,
        )
      }
      revoke()
      const downloadLink = document.createElement('a')
      downloadLink.href = canvas.toDataURL('image/png')
      downloadLink.download = filename
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
    })
    .catch((error) => console.error(error))
}

export interface ChartImage {
  dataUrl: string
  width: number
  height: number
}

// Rasterizes a live chart SVG to a PNG data URL for embedding in an
// exported document. Charts render only in the browser, so this is how the
// report PDF/Word export captures them. Legends draw as HTML overlays
// (not in the SVG); single-series report charts don't use them.
export async function svgToPngDataUrl(
  svgElement: SVGSVGElement,
  {
    scale = 2,
    background = '#ffffff',
  }: { scale?: number; background?: string } = {},
): Promise<ChartImage> {
  const { clone, width, height } = cloneSvgWithStyles(svgElement)
  const { image, revoke } = await svgElementToImage(clone)
  try {
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(width * scale)
    canvas.height = Math.round(height * scale)
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Unable to get canvas context')
    ctx.fillStyle = background
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
    return { dataUrl: canvas.toDataURL('image/png'), width, height }
  } finally {
    revoke()
  }
}

const SVG_FONT_STACK = '"Geist Sans", Arial, Helvetica, sans-serif'

function cloneSvgWithStyles(svgElement: SVGSVGElement): {
  clone: SVGElement
  width: number
  height: number
} {
  const clone = svgElement.cloneNode(true) as SVGElement
  clone.style.color = '#000000'
  clone.style.background = 'transparent'
  clone.style.fontFamily = SVG_FONT_STACK

  // A standalone SVG rasterized through an <img> can't load the Geist
  // webfont, so without a forced sans stack the chart text falls back to
  // the browser default serif. Pin every text node to a system sans.
  const styleElement = document.createElement('style')
  styleElement.textContent = `${collectDocumentStyles()} text,tspan{font-family:${SVG_FONT_STACK} !important}`
  clone.prepend(styleElement)

  const width =
    svgElement.viewBox.baseVal.width || svgElement.width.baseVal.value
  const height =
    svgElement.viewBox.baseVal.height || svgElement.height.baseVal.value
  return { clone, width, height }
}

function collectDocumentStyles(): string {
  return Array.from(document.styleSheets)
    .map((sheet) => {
      try {
        return Array.from(sheet.cssRules)
          .map((rule) => rule.cssText)
          .join('')
      } catch {
        return ''
      }
    })
    .join('')
}

function svgElementToImage(
  clone: SVGElement,
): Promise<{ image: HTMLImageElement; revoke: () => void }> {
  const svgData = new XMLSerializer().serializeToString(clone)
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(svgBlob)
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () =>
      resolve({ image, revoke: () => URL.revokeObjectURL(url) })
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to rasterize chart SVG'))
    }
    image.src = url
  })
}
